import { useEffect, useState } from 'react';
import { getFixOutcomes, unmarkFixed, type FixOutcome } from '../account';
import { Card, SectionLabel } from './ui';

/**
 * Did the changes work?
 *
 * The report used to end at "change this sentence". Nothing followed up, so the
 * client had no way to learn whether their afternoon had bought anything — and
 * that is the only question worth asking of an optimisation tool.
 *
 * Honest about the common case: most marks sit at "not re-measured yet", because
 * off-site and on-page changes take a run to show and weeks to settle. Saying so
 * is better than implying a verdict we have not earned.
 */
const VERDICT: Record<FixOutcome['verdict'], { label: string; cls: string; note: string }> = {
  improved: { label: 'worked', cls: 'text-success border-success/50 bg-success/10', note: 'moved up since you marked it' },
  unchanged: { label: 'no change yet', cls: 'text-estimated border-estimated/50 bg-estimated/10', note: 'measured again, same result' },
  worse: { label: 'slipped', cls: 'text-[#E0574A] border-[#E0574A]/50 bg-[#E0574A]/10', note: 'lower than when you marked it' },
  'not-re-measured': { label: 'not measured yet', cls: 'text-text-secondary border-[#70707D]', note: 'run this question again to find out' },
};

const STATE: Record<string, string> = { mentioned: 'named', cited_only: 'used, not named', absent: 'not named' };

export default function FixOutcomes({ domain, querySet }: { domain: string; querySet?: string }): JSX.Element | null {
  const [outcomes, setOutcomes] = useState<FixOutcome[] | null>(null);

  useEffect(() => {
    if (!domain) { setOutcomes(null); return; }
    let cancelled = false;
    getFixOutcomes(domain, querySet).then((o) => { if (!cancelled) setOutcomes(o); }).catch(() => { if (!cancelled) setOutcomes(null); });
    return () => { cancelled = true; };
  }, [domain, querySet]);

  if (!outcomes || outcomes.length === 0) return null;

  async function drop(q: string): Promise<void> {
    await unmarkFixed(domain, q).catch(() => {});
    setOutcomes((prev) => (prev ?? []).filter((o) => o.query !== q));
  }

  const worked = outcomes.filter((o) => o.verdict === 'improved').length;
  const measured = outcomes.filter((o) => o.verdict !== 'not-re-measured').length;

  return (
    <>
      <SectionLabel>Did your changes work?</SectionLabel>
      <Card className="p-4 mt-3 mb-5">
        <h3 className="font-serif text-lg mb-1">{outcomes.length} {outcomes.length === 1 ? 'change' : 'changes'} you marked as done</h3>
        <p className="text-sm text-text-secondary mb-4">
          {measured === 0
            ? 'None of them have been measured again yet. Run the same question set and this fills in.'
            : <>Of the {measured} re-measured, <span className="text-success">{worked}</span> improved. Only runs made
              <em> after</em> you marked a change are counted; an earlier run cannot say anything about it.</>}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Question</th>
              <th className="pr-3 w-32">When marked</th>
              <th className="pr-3 w-32">Now</th>
              <th className="pr-3 w-36">Verdict</th>
              <th className="w-8"></th>
            </tr></thead>
            <tbody>
              {outcomes.map((o) => (
                <tr key={o.query} className="border-t border-warm-dark-2">
                  <td className="py-2 pr-3 text-text-primary">
                    {o.query}
                    {o.pageUrl && <div className="font-mono text-xs text-text-secondary/70 truncate max-w-[22rem]">{o.pageUrl}</div>}
                  </td>
                  <td className="pr-3 font-mono text-xs text-text-secondary">{STATE[o.stateBefore] ?? o.stateBefore}</td>
                  <td className="pr-3 font-mono text-xs text-text-secondary">{o.stateNow ? (STATE[o.stateNow] ?? o.stateNow) : '-'}</td>
                  <td className="pr-3">
                    <span title={VERDICT[o.verdict].note} className={`font-mono text-[11px] uppercase tracking-wide rounded px-2 py-0.5 border ${VERDICT[o.verdict].cls}`}>
                      {VERDICT[o.verdict].label}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => void drop(o.query)} title="Stop tracking this change"
                      className="font-mono text-xs text-text-secondary/50 hover:text-[#E0574A] transition">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono text-xs text-text-secondary/70 mt-2">
          On-page wording usually moves within a run or two. Off-site work, listings, mentions, profiles, has to be
          crawled and absorbed first, so give it weeks before reading anything into “no change yet”.
        </p>
      </Card>
    </>
  );
}
